import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/** Charles Bridge's axis (m): along the far bank, square to the view as in the owner's photograph, from
 *  edge to edge of the widest stage (owner). */
const BRIDGE_A = { x: 2600, z: 330 };
const BRIDGE_B = { x: -2600, z: 330 };
/** The bridge's deck and parapet heights over the river, its width (m). */
const DECK = 13;
const PARAPET = 14.4;
const BRIDGE_W = 10;
/** The Malá Strana embankment's facades and the castle hill's crest (m from us). */
const BANK_Z = 380;
const HILL_Z = 640;
/** The castle's fronts along the crest (owner's photograph): x from, x to, height, z. */
const CASTLE_FLOOR = 60;
const CASTLE_Z = 615;
const CASTLE_BLOCKS: [ number, number, number, number ][] = [
  [ -1020, -800, 21, 0 ], [ -800, -560, 22, 0 ], [ -560, -330, 23, 0 ], [ -330, -170, 21, 0 ], [ -170, -40, 25, 0 ], [ -40, 60, 22, 0 ], [ 60, 470, 27, 0 ], [ 470, 600, 23, 0 ], [ 600, 840, 21, 0 ],
];
/** St Vitus Cathedral: its west end (x, m) and the line of its south face (z, m), behind the castle's fronts
 *  and left of the hero's words. */
const VITUS = { x: -318, z: 690 };
/** (owner: up at the top left, as large as in the photograph) the cathedral drawn larger than life. */
const VS = 1.65;
/** The swans' water (m from us): before the bridge, near enough to see them. */
const SWAN_NEAR = 26;
const SWAN_FAR = 120;

/** A mute swan on the Vltava, in metres: where it is, which way it drifts, how fast. */
interface Swan {
  x: number;
  z: number;
  dir: 1 | -1;
  speed: number;
  size: number;
  /** Out only while the counter is strong enough for its rank; fading in and out. */
  rank: number;
  shown: number;
  phase: number;
}

/**
 * Prague's hero board (2026-10-18), after the owner's photograph: the Vltava at blue hour from the Old Town's
 * embankment, square across the river, in true perspective (a camera 6 m over the water, everything placed in
 * metres). A deep blue sky; Petřín far off on the left; Hradčany's hill with the castle's long fronts lit gold
 * along its crest and St George's two white towers; the lit houses and dark lindens of Malá Strana along the
 * far bank; Charles Bridge along it on the right on its great arches, floodlit, its statues gold on the
 * parapet and its lamps glowing; the weir's pale line across the river and the river holding all of it in
 * long trembling reflections. Swans drift on the water, more of them on the charge (owner: no boats, no
 * birds in the sky). The page's wallpaper is the swans. Sized by the stage's height.
 */
@Component( {
  selector: 'app-hero-prague',
  template: '<canvas #canvas class="hprg-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hprg-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroPragueComponent extends HeroMotifBase {

  /** Sky, hills, castle, far bank, the river and their reflections. */
  private back: HTMLCanvasElement | null = null;
  /** The same back layer with most windows lit, laid over it as the counter charges (owner: as Tokyo). */
  private backLit: HTMLCanvasElement | null = null;
  /** Charles Bridge and its reflection. */
  private front: HTMLCanvasElement | null = null;
  private swans: Swan[] = [];
  /** Light trembling on the water under the lit things: x, z, phase, size, warmth. */
  private glints: [ number, number, number, number, number ][] = [];
  /** The long columns of light the lamps lay down the water toward us: x, z of the lamp, phase. */
  private columns: [ number, number, number ][] = [];
  /** The bridge's gas lamps' flames: screen x, y, pixels to the metre, phase. */
  private lamps: [ number, number, number, number ][] = [];
  private scaleK = 1;
  private eye = 6;
  private F = 0;
  private hz = 0;
  private vx = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
  }

  /** Project a point: x across (metres from the axis), y up, z away from us. */
  private P( x: number, y: number, z: number ): [ number, number ] {
    return [ this.vx + this.F * x / z, this.hz - this.F * ( y - this.eye ) / z ];
  }

  protected build(): void {
    const W = this.width;
    const H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    // (owner: everything 5% smaller)
    this.F = 1.8 * H * 0.95;
    // (owner: the whole picture 20 px higher)
    this.hz = 0.74 * H - 20;
    this.vx = W / 2;
    this.back = null;
    const r = this.seededLocal( 23 );
    // the swans: a pair or two at rest, a whole bevy on the charge (owner)
    this.swans = [];
    for ( let i = 0; i < 22; i++ ) {
      const z = SWAN_NEAR + Math.pow( r(), 0.8 ) * ( SWAN_FAR - SWAN_NEAR );
      const half = ( W / 2 ) * z / this.F;
      this.swans.push( {
        x: -half + r() * half * 2, z, dir: r() < 0.5 ? -1 : 1, speed: 0.25 + r() * 0.45, size: 0.9 + r() * 0.25,
        rank: i < 2 ? 0 : i < 4 ? 0.05 : 0.1 + r() * 0.9, shown: 0, phase: r() * Math.PI * 2,
      } );
    }
    // (kaizen, owner's photograph) a column of light on the water under every lamp along the bank and the bridge
    this.columns = [];
    const reach = ( W / 2 + 60 ) * BRIDGE_A.z / this.F;
    for ( let x = -reach; x < reach; x += 28 ) { this.columns.push( [ x, BRIDGE_A.z - 6, r() * 10 ] ); }
    // the trembling light on the river under the lit things
    this.glints = [];
    for ( let i = 0; i < 280; i++ ) {
      const z = 60 + Math.pow( r(), 0.8 ) * ( BANK_Z - 80 );
      const half = ( W / 2 ) * z / this.F;
      this.glints.push( [ -half + r() * half * 2, z, r() * Math.PI * 2, 0.6 + r(), r() ] );
    }
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    if ( this.frozen ) {
      this.drawWallSwans( ctx );
      return;
    }
    if ( !this.back ) { this.bake( ctx.getTransform().a ); }
    if ( !this.back || !this.front ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    if ( this.backLit && this.energy > 0.01 ) {
      ctx.globalAlpha = Math.min( 1, this.energy );
      ctx.drawImage( this.backLit, 0, 0, W, H );
      ctx.globalAlpha = 1;
    }
    this.drawGlints( ctx );
    ctx.drawImage( this.front, 0, 0, W, H );
    this.drawLamps( ctx );
    this.moveSwans( dt );
    for ( const s of this.swans.slice().sort( ( a, b ) => b.z - a.z ) ) {
      if ( s.shown < 0.02 ) { continue; }
      const [ px, py ] = this.P( s.x, 0, s.z );
      const k = this.F / s.z * s.size;
      const bob = Math.sin( this.time * W0 * 6 + s.phase ) * k * 0.04;
      ctx.save();
      ctx.globalAlpha = s.shown;
      this.drawSwanRipples( ctx, px, py, k, s );
      ctx.translate( px, py + bob );
      ctx.scale( s.dir, 1 );
      this.swan( ctx, k );
      ctx.restore();
    }
  }

  /** The lamps' and floodlights' gold trembling on the river: short soft streaks that come and go. */
  private drawGlints( ctx: CanvasRenderingContext2D ): void {
    const t = this.time * W0;
    const [ , , warm, gold ] = this.five();
    // the lamps' columns: broken gold dashes from the far shore down toward us, swaying and flickering
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for ( const [ x, z, ph ] of this.columns ) {
      const [ px, py ] = this.P( x, 0, z );
      const len = this.height - py, k = this.F / z;
      for ( let d = 2; d < len * 0.55; d += 2 + d * 0.05 ) {
        const f = d / ( len * 0.55 );
        const a = Math.pow( 0.5 + 0.5 * Math.sin( t * 11 + d * 0.9 + ph ), 1.5 ) * Math.pow( 1 - f, 1.2 ) * ( 0.6 + 0.5 * this.energy );
        if ( a < 0.05 ) { continue; }
        const wd = k * ( 1 + f * 2.2 ) * ( 0.6 + 0.4 * Math.sin( d * 1.7 + ph ) ), sway = Math.sin( t * 5 + d * 0.3 + ph ) * k * 0.6 * f;
        ctx.fillStyle = this.rgba( this.mix( gold, [ 255, 236, 180 ], 0.4 * ( 1 - f ) ), a );
        ctx.fillRect( px + sway - wd / 2, py + d, wd, Math.max( 1.2, 1.2 + f * 1.8 ) );
      }
    }
    ctx.restore();
    for ( const [ x, z, ph, k, w ] of this.glints ) {
      const a = Math.pow( Math.max( 0, Math.sin( t * 7 + ph * 5 ) ), 3 ) * 0.55;
      if ( a < 0.03 ) { continue; }
      const [ px, py ] = this.P( x, 0, z );
      const s = this.F / z * k;
      const wd = Math.max( 1.4, s * 3.2 ), h = Math.max( 0.5, s * 0.35 );
      const col = this.mix( gold, warm, w * 0.6 );
      const g = ctx.createRadialGradient( px, py, 0, px, py, wd );
      g.addColorStop( 0, this.rgba( this.mix( col, [ 255, 246, 220 ], 0.4 ), a ) );
      g.addColorStop( 1, this.rgba( col, 0 ) );
      ctx.save();
      ctx.translate( px, py ); ctx.scale( 1, h / wd ); ctx.translate( -px, -py );
      ctx.fillStyle = g;
      ctx.fillRect( px - wd, py - wd, wd * 2, wd * 2 );
      ctx.restore();
    }
  }

  /**
   * The gas lamps' flames: each flickers on its own, a bright small heart in the glass and a soft warm halo
   * the size of the lantern's light; on the charge they burn up stronger (owner).
   */
  private drawLamps( ctx: CanvasRenderingContext2D ): void {
    const t = this.time * W0;
    const [ , , warm, gold ] = this.five();
    const e = this.energy;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for ( const [ x, y, k, ph ] of this.lamps ) {
      const flick = 0.82 + 0.1 * Math.sin( t * 37 + ph ) + 0.08 * Math.sin( t * 61 + ph * 2.3 ) * Math.sin( t * 13 + ph );
      const p = ( 0.55 + 0.75 * e ) * flick;
      const r = k * ( 1.6 + 1.6 * e ) * ( 0.94 + 0.06 * flick );
      const halo = ctx.createRadialGradient( x, y, 0, x, y, r );
      halo.addColorStop( 0, this.rgba( [ 255, 236, 190 ], 0.75 * p ) );
      halo.addColorStop( 0.22, this.rgba( gold, 0.42 * p ) );
      halo.addColorStop( 1, this.rgba( warm, 0 ) );
      ctx.fillStyle = halo;
      ctx.fillRect( x - r, y - r, r * 2, r * 2 );
      // the flame itself: a small bright tongue that sways
      const fx = x + Math.sin( t * 29 + ph ) * k * 0.04, fh = k * ( 0.32 + 0.12 * e ) * flick;
      ctx.fillStyle = this.rgba( [ 255, 248, 220 ], Math.min( 1, 0.8 * p + 0.2 ) );
      ctx.beginPath(); ctx.ellipse( fx, y + k * 0.06, k * 0.09, fh, 0, 0, Math.PI * 2 ); ctx.fill();
    }
    ctx.restore();
  }

  /** The swans drift slowly along the river; those the counter calls out fade in where they float. */
  private moveSwans( dt: number ): void {
    const share = 0.06 + 0.94 * this.energy;
    for ( const s of this.swans ) {
      const want = s.rank <= share ? 1 : 0;
      if ( dt > 0 && want && s.shown < 0.02 ) {
        const half = ( this.width / 2 ) * s.z / this.F;
        s.x = -half * 0.9 + Math.random() * half * 1.8;
      }
      s.shown = dt > 0 ? s.shown + ( want - s.shown ) * Math.min( 1, dt * 0.5 ) : want;
      if ( dt <= 0 ) { continue; }
      s.x += s.dir * s.speed * dt;
      const half = ( this.width / 2 + 30 ) * s.z / this.F;
      if ( s.x < -half || s.x > half ) { s.dir = s.dir === 1 ? -1 : 1; }
    }
  }

  /** The faint rings a swan leaves on the water as it paddles. */
  private drawSwanRipples( ctx: CanvasRenderingContext2D, px: number, py: number, k: number, s: Swan ): void {
    const t = this.time * W0 * 3 + s.phase;
    for ( let q = 0; q < 3; q++ ) {
      const f = ( ( t / ( Math.PI * 2 ) + q / 3 ) % 1 );
      const rx = k * ( 0.9 + f * 2.4 ), ry = rx * 0.16;
      ctx.strokeStyle = `rgba(210,226,255,${ 0.28 * ( 1 - f ) })`;
      ctx.lineWidth = Math.max( 0.5, k * 0.05 );
      ctx.beginPath(); ctx.ellipse( px - s.dir * k * f * 0.8, py + k * 0.02, rx, ry, 0, 0, Math.PI * 2 ); ctx.stroke();
    }
    // its reflection: a soft pale smudge under it
    const g = ctx.createRadialGradient( px, py + k * 0.25, 0, px, py + k * 0.25, k * 1.1 );
    g.addColorStop( 0, 'rgba(230,236,250,0.22)' );
    g.addColorStop( 1, 'rgba(230,236,250,0)' );
    ctx.fillStyle = g;
    ctx.save(); ctx.translate( px, py + k * 0.25 ); ctx.scale( 1, 0.3 ); ctx.translate( -px, -( py + k * 0.25 ) );
    ctx.fillRect( px - k * 1.1, py + k * 0.25 - k * 1.1, k * 2.2, k * 2.2 );
    ctx.restore();
  }

  /**
   * One mute swan afloat, facing right, its waterline at y 0 (y down, k pixels to the metre): the long
   * white body riding high with its wings folded up over the back, the neck in a gentle S, the head with
   * its orange bill and black knob; warm white where the lamps light it, cool blue grey in its shade.
   */
  private swan( ctx: CanvasRenderingContext2D, k: number ): void {
    const lit = 'rgb(246,240,228)', shade = 'rgb(170,182,206)';
    // the body: a long boat of a shape, the folded wings raised in a soft hump at the back
    const body = ctx.createLinearGradient( 0, -k * 0.55, 0, 0 );
    body.addColorStop( 0, lit );
    body.addColorStop( 1, shade );
    ctx.fillStyle = body;
    ctx.beginPath();
    ctx.moveTo( k * 0.45, 0 );
    ctx.bezierCurveTo( k * 0.55, -k * 0.22, k * 0.3, -k * 0.34, k * 0.05, -k * 0.36 );
    ctx.bezierCurveTo( -k * 0.3, -k * 0.6, -k * 0.62, -k * 0.5, -k * 0.74, -k * 0.24 );
    ctx.quadraticCurveTo( -k * 0.8, -k * 0.08, -k * 0.62, 0 );
    ctx.closePath();
    ctx.fill();
    // the neck rising from the breast in an S, then the head
    ctx.strokeStyle = lit;
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max( 0.8, k * 0.12 );
    ctx.beginPath();
    ctx.moveTo( k * 0.34, -k * 0.2 );
    ctx.bezierCurveTo( k * 0.52, -k * 0.42, k * 0.26, -k * 0.62, k * 0.34, -k * 0.86 );
    ctx.stroke();
    ctx.fillStyle = lit;
    ctx.beginPath(); ctx.ellipse( k * 0.4, -k * 0.9, k * 0.09, k * 0.065, 0.2, 0, Math.PI * 2 ); ctx.fill();
    ctx.fillStyle = 'rgb(232,120,50)';
    ctx.beginPath(); ctx.moveTo( k * 0.46, -k * 0.93 ); ctx.lineTo( k * 0.6, -k * 0.86 ); ctx.lineTo( k * 0.46, -k * 0.85 ); ctx.closePath(); ctx.fill();
    ctx.fillStyle = 'rgb(30,30,34)';
    ctx.beginPath(); ctx.arc( k * 0.46, -k * 0.92, k * 0.03, 0, Math.PI * 2 ); ctx.fill();
  }

  /** The wallpaper: the swans, still, in the theme's colours, scattered down the page. */
  private drawWallSwans( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const r = this.seededLocal( 7 );
    const n = Math.max( 5, Math.round( W * H / 80000 ) );
    for ( let i = 0; i < n; i++ ) {
      const x = r() * W, y = r() * H, k = ( 22 + r() * 16 ) * this.scaleK;
      ctx.save();
      ctx.globalAlpha = 0.4 + r() * 0.3;
      ctx.translate( x, y );
      ctx.scale( r() < 0.5 ? -1 : 1, 1 );
      this.swan( ctx, k );
      ctx.restore();
    }
  }

  /**
   * The still parts, painted once, far to near, in metres projected. Every object is drawn twice, the second
   * time mirrored in the river (y turned under the water) onto its own canvas, which is laid on the water in
   * thin trembling bands. The back layer: the sky, Petřín, Hradčany's hill and the castle, the far bank; the
   * front layer: Charles Bridge.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const hz = this.hz;
    const F = this.F;
    const [ navy, blue, warm, gold, pale ] = this.five();
    const canvas = () => {
      const c = document.createElement( 'canvas' );
      c.width = Math.ceil( W * scale );
      c.height = Math.ceil( H * scale );
      const g2 = c.getContext( '2d' )!;
      g2.scale( scale, scale );
      return { c, g: g2 };
    };
    let g: CanvasRenderingContext2D;
    let mirror = false;
    // (owner) every window dim and few at rest; the bright copy, baked with this set, has most of them lit
    let bright = false;
    const P = ( x: number, y: number, z: number ) => this.P( x, mirror ? -y : y, z );
    const poly = ( pts: [ number, number ][], fill: string | CanvasGradient ) => {
      g.fillStyle = fill;
      g.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? g.lineTo( x, y ) : g.moveTo( x, y ) );
      g.closePath();
      g.fill();
    };
    // the blue hour's air: far things steeped toward the sky near the horizon
    const air = this.mix( navy, blue, 0.35 );
    const inAir = ( col: RGB, z: number, k = 0.85 ) => this.mix( col, air, ( 1 - Math.exp( -z / 2600 ) ) * k );
    // floodlit stone: gold at the foot where the lamps are, warmer and darker up the wall
    const lit = ( y0: number, y1: number, z: number, x: number, k = 1 ): CanvasGradient => {
      const a = P( x, y0, z ), b = P( x, y1, z );
      const gr = g.createLinearGradient( 0, a[ 1 ], 0, b[ 1 ] );
      gr.addColorStop( 0, this.rgba( inAir( this.mix( this.mix( gold, [ 255, 214, 130 ], 0.4 ), warm, 0.08 ), z, 0.3 * k ), 1 ) );
      gr.addColorStop( 0.6, this.rgba( inAir( this.mix( gold, warm, 0.45 ), z, 0.3 * k ), 1 ) );
      gr.addColorStop( 1, this.rgba( inAir( this.mix( warm, [ 70, 36, 30 ], 0.45 ), z, 0.3 * k ), 1 ) );
      return gr;
    };
    const rr = ( seed: number ) => this.seededLocal( seed );
    const half = ( z: number ) => ( W / 2 + 60 ) * z / F;

    // ---- the far things, drawn once straight and once mirrored ----
    const far = () => {
      // Petřín: the wooded hill far off on the left, dark blue in the air
      {
        const z = 1400, pts: [ number, number ][] = [];
        for ( let x = -half( z ); x <= 200; x += 20 ) {
          const t = ( x + 900 ) / 560;
          const y = 130 * Math.exp( -t * t * 1.2 ) + 10 + Math.sin( x * 0.05 ) * 2;
          pts.push( P( x, y, z ) );
        }
        pts.push( P( 200, 0, z ), P( -half( z ), 0, z ) );
        poly( pts, this.rgba( inAir( [ 16, 30, 42 ], z, 0.75 ), 1 ) );
      }
      // Hradčany's hill: rising from the left to the castle's crest, falling away again to the right
      const ridge = ( x: number ) => {
        // (owner: on a wide stage the fronts at the ends hung in the air) the crest runs on past every front
        const up = 1 / ( 1 + Math.exp( -( x + 1080 ) / 60 ) ), down = 1 / ( 1 + Math.exp( ( x - 900 ) / 60 ) );
        return 16 + 44 * up * down + 10 * down * ( 1 - up ) + Math.sin( x * 0.07 ) * 1.5;
      };
      {
        const z = HILL_Z, pts: [ number, number ][] = [];
        for ( let x = -half( z ); x <= half( z ); x += 12 ) { pts.push( P( x, ridge( x ), z ) ); }
        pts.push( P( half( z ), 0, z ), P( -half( z ), 0, z ) );
        poly( pts, this.rgba( inAir( [ 18, 32, 40 ], z, 0.55 ), 1 ) );
        // (kaizen: draw order) St Vitus and the castle on the crest first, then the slope's trees and roofs before it
        // (owner: draw it yourself) St Vitus Cathedral behind the castle's fronts, measured off the owner's
        // photograph (0.32 m to its pixel) and set at its real size: the twin west towers and their spires
        // (80 and 78 m), the long nave under its steep dark roof (eaves 30 m, ridge 46 m) with its lit windows,
        // buttresses and pinnacles, the south transept's gable, the slender crossing spire (77 m) and the great
        // south tower before them all: its body and tall window, the gold gallery, the Renaissance helm of bells
        // and lanterns up to 96.6 m. Floodlit gold from below, the roofs and helms dark
        {
          // (owner: 40px up, then 20px back down, at 1440x900) its foot 11.5 m higher, still behind the fronts
          const U0 = VITUS.x, Z = VITUS.z, fl = CASTLE_FLOOR + 11.5;
          // (owner: its parts slipped apart) drawn as one elevation at a single depth: the parts' small offsets
          // in depth only order them, so the perspective cannot slide one past another
          const C3 = ( u: number, y: number, _dz = 0 ) => P( U0 + u * VS, fl + y * VS, Z );
          const shape = ( pts: [ number, number ][], fill: string | CanvasGradient, dz = 0 ) => poly( pts.map( ( [ u, y ] ) => C3( u, y, dz ) ), fill );
          const glow = ( y0: number, y1: number, _dz = 0, k = 1 ) => lit( fl + y0 * VS, fl + y1 * VS, Z, U0, 0.7 * k );
          const dark = ( c: RGB, a = 1 ) => this.rgba( inAir( c, Z, 0.45 ), a );
          const roofDark: RGB = [ 58, 48, 52 ], helm: RGB = [ 44, 58, 56 ];
          // a spire: a tall narrow pyramid, lit gold at its foot fading to dark bronze, small crockets up its edges
          const spire = ( uc: number, y0: number, wd: number, tip: number, dz = 0 ) => {
            const a = C3( uc, y0, dz ), b = C3( uc, tip, dz );
            const gr = g.createLinearGradient( 0, a[ 1 ], 0, b[ 1 ] );
            gr.addColorStop( 0, this.rgba( inAir( this.mix( gold, warm, 0.4 ), Z, 0.4 ), 1 ) );
            gr.addColorStop( 1, this.rgba( inAir( [ 70, 46, 40 ], Z, 0.45 ), 1 ) );
            shape( [ [ uc - wd / 2, y0 ], [ uc + wd / 2, y0 ], [ uc, tip ] ], gr, dz );
            // (kaizen 2) its two faces we see: the right one in shade, a lit rib up the corner between them
            shape( [ [ uc, y0 ], [ uc + wd / 2, y0 ], [ uc, tip ] ], dark( [ 30, 16, 14 ], 0.34 ), dz );
            shape( [ [ uc - Math.max( 0.1, wd * 0.025 ), y0 ], [ uc + Math.max( 0.1, wd * 0.025 ), y0 ], [ uc, tip ] ], this.rgba( inAir( this.mix( gold, [ 255, 236, 190 ], 0.3 ), Z, 0.35 ), 0.75 ), dz );
            for ( let y = y0 + 2.4; y < tip - 2; y += 2.6 ) {
              const t = ( y - y0 ) / ( tip - y0 ), hw = wd / 2 * ( 1 - t );
              for ( const sd of [ -1, 1 ] ) { shape( [ [ uc + sd * hw, y ], [ uc + sd * ( hw + 0.7 ), y + 0.5 ], [ uc + sd * ( hw - 0.2 ), y + 1.3 ] ], gr, dz ); }
            }
            // the open stonework: dark bands of small openings up the needle
            for ( let y = y0 + 3; y < tip - 6; y += 4.2 ) {
              const t = ( y - y0 ) / ( tip - y0 ), hw = wd / 2 * ( 1 - t );
              shape( [ [ uc - hw * 0.8, y ], [ uc + hw * 0.8, y ], [ uc + hw * 0.7, y + 0.6 ], [ uc - hw * 0.7, y + 0.6 ] ], dark( [ 60, 36, 30 ], 0.6 ), dz - 0.05 );
              for ( const sd of [ -0.35, 0.35 ] ) { shape( [ [ uc + sd * hw * 2 - 0.15, y + 0.8 ], [ uc + sd * hw * 2 + 0.15, y + 0.8 ], [ uc + sd * hw * 2, y + 2.2 ] ], dark( [ 60, 36, 30 ], 0.7 ), dz - 0.05 ); }
            }
            shape( [ [ uc - 0.25, tip ], [ uc + 0.25, tip ], [ uc, tip + 1.6 ] ], dark( [ 90, 70, 50 ] ), dz );
          };
          // (owner: more detail, majestic) a string course: a light ledge with its shadow under it
          const course = ( u0: number, u1: number, y: number, dz = 0 ) => {
            shape( [ [ u0 - 0.3, y ], [ u1 + 0.3, y ], [ u1 + 0.3, y + 0.55 ], [ u0 - 0.3, y + 0.55 ] ], this.rgba( inAir( this.mix( gold, [ 255, 236, 190 ], 0.4 ), Z, 0.3 ), 1 ), dz - 0.2 );
            shape( [ [ u0, y - 0.35 ], [ u1, y - 0.35 ], [ u1, y ], [ u0, y ] ], dark( [ 70, 40, 34 ], 0.55 ), dz - 0.2 );
          };
          // blind tracery: tall shallow panels, a shade darker, each under a pointed head
          const panels = ( u0: number, u1: number, y0: number, y1: number, n: number, dz = 0 ) => {
            const w = ( u1 - u0 ) / n;
            for ( let q = 0; q < n; q++ ) {
              const a = u0 + q * w + w * 0.18, b = u0 + ( q + 1 ) * w - w * 0.18, m = ( a + b ) / 2;
              shape( [ [ a, y0 ], [ b, y0 ], [ b, y1 - ( b - a ) * 0.7 ], [ m, y1 ], [ a, y1 - ( b - a ) * 0.7 ] ], dark( this.mix( warm, [ 90, 50, 36 ], 0.3 ), 0.35 ), dz - 0.1 );
            }
          };
          // (kaizen 1) a tower's volume: lit a little at its left from the floodlights, falling into shade at its right
          const volume = ( u0: number, u1: number, y0: number, y1: number, dz = 0 ) => {
            const a = C3( u0, y0, dz ), b = C3( u1, y0, dz );
            const gr = g.createLinearGradient( a[ 0 ], 0, b[ 0 ], 0 );
            gr.addColorStop( 0, 'rgba(255,240,210,0.12)' );
            gr.addColorStop( 0.45, 'rgba(0,0,0,0)' );
            gr.addColorStop( 1, 'rgba(34,16,12,0.3)' );
            shape( [ [ u0, y0 ], [ u1, y0 ], [ u1, y1 ], [ u0, y1 ] ], gr, dz );
          };
          const pinnacle = ( uc: number, y0: number, h: number, wd = 1.2, dz = 0 ) => shape( [ [ uc - wd / 2, y0 ], [ uc + wd / 2, y0 ], [ uc, y0 + h ] ], glow( y0, y0 + h, dz, 1.1 ), dz );
          // a pointed window: dark gold glass in its frame
          const lancet = ( u0: number, u1: number, y0: number, y1: number, dz = 0 ) => {
            const m = ( u0 + u1 ) / 2, w = u1 - u0, pts: [ number, number ][] = [ [ u0, y0 ], [ u1, y0 ], [ u1, y1 - w * 0.8 ] ];
            for ( let q = 0; q <= 8; q++ ) { const a = q / 8 * Math.PI; pts.push( [ m + Math.cos( a ) * w / 2, y1 - w * 0.8 + Math.sin( a ) * w * 0.8 ] ); }
            pts.push( [ u0, y1 - w * 0.8 ] );
            // (kaizen 3) the glass: warm with the light inside at its foot, darkening up to its head
            const a = C3( m, y0, dz ), b = C3( m, y1, dz );
            const gr = g.createLinearGradient( 0, a[ 1 ], 0, b[ 1 ] );
            gr.addColorStop( 0, this.rgba( inAir( this.mix( gold, warm, 0.35 ), Z, 0.35 ), 1 ) );
            gr.addColorStop( 0.45, this.rgba( inAir( this.mix( warm, [ 110, 60, 40 ], 0.45 ), Z, 0.4 ), 1 ) );
            gr.addColorStop( 1, this.rgba( inAir( [ 72, 40, 34 ], Z, 0.4 ), 1 ) );
            shape( pts, gr, dz );
            if ( w > 2.6 ) { shape( [ [ m - 0.13, y0 ], [ m + 0.13, y0 ], [ m + 0.13, y1 - w * 0.75 ], [ m - 0.13, y1 - w * 0.75 ] ], glow( y0, y1, dz, 0.95 ), dz - 0.05 ); }
          };
          // the twin west towers: their bodies, open belfries, corner pinnacles and spires
          for ( const [ u0, u1, top, bell, tip ] of [ [ 0, 15.6, 43, 56, 80 ], [ 17, 27, 42, 54, 78 ] ] ) {
            shape( [ [ u0, 0 ], [ u1, 0 ], [ u1, top ], [ u0, top ] ], glow( 0, top ) );
            volume( u0, u1, 0, top );
            const b0 = u0 + ( u1 - u0 ) * 0.14, b1 = u1 - ( u1 - u0 ) * 0.14;
            shape( [ [ b0, top ], [ b1, top ], [ b1, bell ], [ b0, bell ] ], glow( top, bell, 0, 0.9 ) );
            volume( b0, b1, top, bell );
            lancet( b0 + 1, ( b0 + b1 ) / 2 - 0.4, top + 2, bell - 1.5 );
            lancet( ( b0 + b1 ) / 2 + 0.4, b1 - 1, top + 2, bell - 1.5 );
            panels( u0 + 0.6, u1 - 0.6, 2, 12, 3 );
            lancet( u0 + 2, u1 - 2, 14, 36 );
            panels( u0 + 0.6, u1 - 0.6, 37, top - 1, 4 );
            for ( const y of [ 12.5, 36.5, top - 0.5 ] ) { course( u0, u1, y ); }
            for ( const pu of [ u0 + 0.6, u1 - 0.6 ] ) { pinnacle( pu, top, 10, 1.4 ); }
            for ( const pu of [ b0 + 0.4, b1 - 0.4 ] ) { pinnacle( pu, bell, 6, 1, -0.1 ); }
            spire( ( u0 + u1 ) / 2, bell, ( b1 - b0 ) * 0.9, tip );
          }
          // the nave's steep roof, eaves to ridge, hipped at the choir's end
          {
            const a = C3( 70, 30 ), b = C3( 70, 45.6, 16 );
            const gr = g.createLinearGradient( 0, a[ 1 ], 0, b[ 1 ] );
            gr.addColorStop( 0, dark( this.mix( roofDark, warm, 0.25 ) ) );
            gr.addColorStop( 1, dark( roofDark ) );
            poly( [ C3( 27, 30 ), C3( 124, 30 ), C3( 116, 45.6, 16 ), C3( 27, 45.6, 16 ) ], gr );
            // (kaizen 5) the light along the eaves and the gilded cresting along the ridge
            shape( [ [ 27, 30 ], [ 124, 30 ], [ 124, 30.6 ], [ 27, 30.6 ] ], this.rgba( inAir( this.mix( gold, [ 255, 236, 190 ], 0.35 ), Z, 0.3 ), 1 ), 0.1 );
            for ( let u = 29; u < 115; u += 2.4 ) { shape( [ [ u - 0.25, 45.6 ], [ u + 0.25, 45.6 ], [ u, 47 ] ], this.rgba( inAir( gold, Z, 0.35 ), 0.9 ), 16 ); }
            // its glazed tiles' faint diamond pattern
            g.save();
            g.beginPath();
            [ C3( 27, 30 ), C3( 124, 30 ), C3( 116, 45.6, 16 ), C3( 27, 45.6, 16 ) ].forEach( ( [ x, y ], q ) => q ? g.lineTo( x, y ) : g.moveTo( x, y ) );
            g.closePath();
            g.clip();
            g.strokeStyle = this.rgba( inAir( this.mix( gold, roofDark, 0.6 ), Z, 0.4 ), 0.18 );
            g.lineWidth = Math.max( 0.4, F / Z * VS * 0.12 );
            for ( let u = 0; u < 140; u += 4 ) {
              for ( const sd of [ -1, 1 ] ) {
                const a = C3( u, 30 ), b = C3( u + sd * 10, 45.6, 16 );
                g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.lineTo( b[ 0 ], b[ 1 ] ); g.stroke();
              }
            }
            g.restore();
          }
          // the nave and choir in two storeys: the high clerestory with its tall windows set back, the lower
          // aisle wall before it with its own windows; in every bay a buttress rising to a cluster of
          // pinnacles and two flying arches leaping from it to the clerestory
          shape( [ [ 27, 14 ], [ 124, 14 ], [ 124, 30 ], [ 27, 30 ] ], glow( 14, 30, 6 ), 6 );
          for ( let u = 58; u < 122; u += 7.6 ) { lancet( u + 1.5, u + 5.2, 17, 28.6, 6 ); }
          course( 27, 124, 29, 6 );
          shape( [ [ 27, 0 ], [ 124, 0 ], [ 124, 15 ], [ 27, 15 ] ], glow( 0, 15 ) );
          course( 27, 124, 14.5 );
          for ( let u = 58; u < 122; u += 7.6 ) {
            lancet( u + 1.8, u + 4.9, 3.5, 12.5 );
            shape( [ [ u - 0.8, 0 ], [ u + 0.8, 0 ], [ u + 0.8, 22 ], [ u - 0.8, 22 ] ], glow( 0, 22, -1, 1.2 ), -1 );
            pinnacle( u, 22, 7.5, 1.5, -1 );
            pinnacle( u - 0.9, 21, 4.5, 0.8, -1.1 );
            pinnacle( u + 0.9, 21, 4.5, 0.8, -1.1 );
            g.strokeStyle = this.rgba( inAir( this.mix( gold, warm, 0.3 ), Z, 0.4 ), 1 );
            g.lineWidth = Math.max( 0.6, F / Z * VS * 0.45 );
            for ( const [ y0, y1, lift ] of [ [ 20, 27.5, 3 ], [ 16.5, 22, 2.2 ] ] ) {
              const p0 = C3( u + 0.6, y0, -1 ), p1 = C3( u + 2.4, y1 + lift, 2.5 ), p2 = C3( u + 3.6, y1, 6 );
              g.beginPath(); g.moveTo( p0[ 0 ], p0[ 1 ] ); g.quadraticCurveTo( p1[ 0 ], p1[ 1 ], p2[ 0 ], p2[ 1 ] ); g.stroke();
            }
          }
          // (owner: the spikes right of it hung in the air) the choir's pinnacles stand on its wall, and the east
          // end's corner buttress rises from the ground to carry the last of them
          shape( [ [ 122.4, 0 ], [ 124.6, 0 ], [ 124.6, 31 ], [ 122.4, 31 ] ], glow( 0, 31, -1, 1.15 ), -1 );
          for ( const u of [ 112, 116.5, 120.5, 123.5 ] ) { pinnacle( u, 29.5, 8, 1.1, -1.2 ); }
          // the south transept's gable, lit, its rose window dark gold
          shape( [ [ 49, 30 ], [ 60, 30 ], [ 54.5, 48.8 ] ], glow( 30, 49, -2, 1.1 ), -2 );
          {
            const [ cx, cy ] = C3( 54.5, 37, -2.1 ), k = F / Z * VS;
            g.fillStyle = this.rgba( inAir( this.mix( warm, [ 110, 60, 40 ], 0.4 ), Z, 0.4 ), 1 );
            g.beginPath(); g.arc( cx, cy, k * 2.6, 0, Math.PI * 2 ); g.fill();
          }
          for ( const u of [ 49.3, 59.7 ] ) { pinnacle( u, 30, 12, 1.2, -2 ); }
          shape( [ [ 54.35, 48.6 ], [ 54.65, 48.6 ], [ 54.65, 51.5 ], [ 54.35, 51.5 ] ], this.rgba( inAir( gold, Z, 0.3 ), 1 ), -2.1 );
          shape( [ [ 53.8, 50.2 ], [ 55.2, 50.2 ], [ 55.2, 50.6 ], [ 53.8, 50.6 ] ], this.rgba( inAir( gold, Z, 0.3 ), 1 ), -2.1 );
          // the Golden Gate below it: three pointed arches of the south porch, dark gold within
          shape( [ [ 48.5, 0 ], [ 60.5, 0 ], [ 60.5, 15 ], [ 48.5, 15 ] ], glow( 0, 15, -3, 1.2 ), -3 );
          for ( const a of [ 49.4, 53.1, 56.8 ] ) { lancet( a, a + 2.8, 0, 11, -3 ); }
          course( 48.5, 60.5, 14.6, -3 );
          // the crossing spire: a slim lantern on the ridge and its tall needle
          shape( [ [ 67.2, 45 ], [ 70, 45 ], [ 70, 56 ], [ 67.2, 56 ] ], glow( 45, 56, 8, 0.9 ), 8 );
          spire( 68.6, 56, 3, 77.5, 8 );
          // the great south tower before the nave
          {
            const dz = -10, t0 = 29, t1 = 54, gal = 57;
            shape( [ [ t0, 0 ], [ t1, 0 ], [ t1, gal ], [ t0, gal ] ], glow( 0, gal, dz ), dz );
            volume( t0, t1, 0, gal, dz );
            for ( const [ a, b ] of [ [ t0, t0 + 2.6 ], [ t1 - 2.6, t1 ] ] ) {
              shape( [ [ a, 0 ], [ b, 0 ], [ b, gal ], [ a, gal ] ], glow( 0, gal, dz, 1.25 ), dz - 0.5 );
            }
            panels( t0 + 2.8, t1 - 2.8, 2, 16, 5, dz );
            for ( const y of [ 16.5, 49, 55.5 ] ) { course( t0, t1, y, dz ); }
            lancet( 36, 47, 19, 47, dz );
            // its tracery: two lights under a rose in the window's head, the stone between them lit
            shape( [ [ 41.2, 19 ], [ 41.8, 19 ], [ 41.8, 38.5 ], [ 41.2, 38.5 ] ], glow( 19, 39, dz, 0.9 ), dz - 0.2 );
            for ( const [ a, b ] of [ [ 37.2, 40.8 ], [ 42.2, 45.8 ] ] ) {
              const m2 = ( a + b ) / 2;
              shape( [ [ a, 36.5 ], [ m2, 39.5 ], [ b, 36.5 ], [ b, 37.2 ], [ m2, 40.2 ], [ a, 37.2 ] ], glow( 36, 40, dz, 0.9 ), dz - 0.2 );
            }
            {
              const [ rx, ry ] = C3( 41.5, 42.6, dz - 0.2 ), k = F / Z * VS;
              g.strokeStyle = this.rgba( inAir( this.mix( gold, warm, 0.25 ), Z, 0.35 ), 1 );
              g.lineWidth = Math.max( 0.5, k * 0.45 );
              g.beginPath(); g.arc( rx, ry, k * 2.3, 0, Math.PI * 2 ); g.stroke();
              for ( let q = 0; q < 6; q++ ) { const a = q / 6 * Math.PI * 2; g.beginPath(); g.moveTo( rx, ry ); g.lineTo( rx + Math.cos( a ) * k * 2.3, ry + Math.sin( a ) * k * 2.3 ); g.stroke(); }
            }
            {
              const [ cx, cy ] = C3( 41.5, 51.5, dz - 0.2 ), k = F / Z * VS;
              g.fillStyle = this.rgba( inAir( [ 40, 50, 60 ], Z, 0.4 ), 1 );
              g.beginPath(); g.arc( cx, cy, k * 2.2, 0, Math.PI * 2 ); g.fill();
              g.fillStyle = this.rgba( inAir( gold, Z, 0.3 ), 1 );
              g.beginPath(); g.arc( cx, cy, k * 0.6, 0, Math.PI * 2 ); g.fill();
            }
            // the gold gallery round the top, its corner turrets with their small domes
            shape( [ [ t0 - 0.6, gal ], [ t1 + 0.6, gal ], [ t1 + 0.6, gal + 3 ], [ t0 - 0.6, gal + 3 ] ], this.rgba( inAir( this.mix( gold, [ 255, 236, 180 ], 0.35 ), Z, 0.3 ), 1 ), dz - 0.6 );
            // its balustrade: the dark gaps between the balusters
            for ( let u = t0 + 0.4; u < t1 - 0.4; u += 1.15 ) { shape( [ [ u, gal + 0.7 ], [ u + 0.45, gal + 0.7 ], [ u + 0.45, gal + 2.3 ], [ u, gal + 2.3 ] ], dark( [ 70, 40, 30 ], 0.6 ), dz - 0.7 ); }
            for ( const u of [ t0 + 1, t1 - 1 ] ) {
              shape( [ [ u - 1.1, gal + 3 ], [ u + 1.1, gal + 3 ], [ u + 1.1, gal + 6 ], [ u - 1.1, gal + 6 ] ], glow( gal, gal + 6, dz, 1.1 ), dz - 0.6 );
              const [ cx, cy ] = C3( u, gal + 6.6, dz - 0.6 ), k = F / Z * VS;
              g.fillStyle = dark( this.mix( helm, gold, 0.25 ) );
              g.beginPath(); g.ellipse( cx, cy, k * 1.4, k * 1.1, 0, 0, Math.PI * 2 ); g.fill();
              shape( [ [ u - 0.2, gal + 7.4 ], [ u + 0.2, gal + 7.4 ], [ u, gal + 10 ] ], dark( [ 90, 70, 50 ] ), dz - 0.6 );
            }
            // the helm: a broad bell, the open lantern, a smaller bell, the upper lantern and the needle
            const bell = ( y0: number, y1: number, w0: number, w1: number ) => {
              const pts: [ number, number ][] = [];
              for ( let q = 0; q <= 10; q++ ) {
                const t = q / 10, w = w1 + ( w0 - w1 ) * Math.pow( 1 - t, 1.8 ) * ( 1 - 0.25 * Math.sin( Math.PI * t ) );
                pts.push( [ 41.5 - w / 2, y0 + ( y1 - y0 ) * t ] );
              }
              const back = pts.map( ( [ u, y ] ) => [ 83 - u, y ] as [ number, number ] ).reverse();
              const a = C3( 41.5, y0, dz ), b = C3( 41.5, y1, dz );
              const gr = g.createLinearGradient( 0, a[ 1 ], 0, b[ 1 ] );
              gr.addColorStop( 0, this.rgba( inAir( this.mix( helm, gold, 0.45 ), Z, 0.4 ), 1 ) );
              gr.addColorStop( 1, dark( helm ) );
              shape( [ ...pts, ...back ], gr, dz );
              // (kaizen 4) its copper ribs curving up with it, and the gold ring at its foot
              g.strokeStyle = this.rgba( inAir( this.mix( helm, gold, 0.6 ), Z, 0.35 ), 0.6 );
              g.lineWidth = Math.max( 0.4, F / Z * VS * 0.14 );
              for ( const rf of [ 0.22, 0.5, 0.78 ] ) {
                g.beginPath();
                pts.forEach( ( [ u, y ], q ) => { const w = ( 41.5 - u ) * 2, [ x, yy ] = C3( u + w * rf, y, dz ); q ? g.lineTo( x, yy ) : g.moveTo( x, yy ); } );
                g.stroke();
              }
              shape( [ [ 41.5 - w0 / 2, y0 ], [ 41.5 + w0 / 2, y0 ], [ 41.5 + w0 / 2, y0 + 0.6 ], [ 41.5 - w0 / 2, y0 + 0.6 ] ], this.rgba( inAir( this.mix( gold, [ 255, 236, 180 ], 0.3 ), Z, 0.3 ), 1 ), dz - 0.1 );
            };
            bell( gal + 3, 66, 20, 9 );
            shape( [ [ 38, 66 ], [ 45, 66 ], [ 45, 72 ], [ 38, 72 ] ], glow( 66, 72, dz, 0.9 ), dz );
            for ( const u of [ 38.8, 41, 43.2 ] ) { shape( [ [ u, 67.2 ], [ u + 1, 67.2 ], [ u + 1, 71 ], [ u, 71 ] ], dark( [ 60, 40, 36 ] ), dz - 0.1 ); }
            bell( 72, 77, 8, 3.2 );
            shape( [ [ 40.2, 77 ], [ 42.8, 77 ], [ 42.8, 82 ], [ 40.2, 82 ] ], glow( 77, 82, dz, 0.8 ), dz );
            bell( 82, 85, 3.6, 1.2 );
            spire( 41.5, 85, 1.4, 95, dz );
            const [ cx, cy ] = C3( 41.5, 95.3, dz ), k = F / Z * VS;
            g.fillStyle = this.rgba( inAir( gold, Z, 0.3 ), 1 );
            g.beginPath(); g.arc( cx, cy, k * 0.45, 0, Math.PI * 2 ); g.fill();
            shape( [ [ 41.35, 95.5 ], [ 41.65, 95.5 ], [ 41.65, 96.6 ], [ 41.35, 96.6 ] ], this.rgba( inAir( gold, Z, 0.3 ), 1 ), dz );
          }
        }
        // the castle along the crest (owner's photograph): its long fronts side by side, each its own height
        // and a little in or out, four or five floors of windows, lit gold from below, the roofs dark; St
        // George's two white towers rising behind the middle; the dark tower at the east end
        {
          const r = rr( 53 ), floor = CASTLE_FLOOR;
          // (owner: the two towers behind the logo) St George's towers left out
        for ( const [ x0, x1, height, dz ] of CASTLE_BLOCKS ) {
            // (owner: all the fronts joined, no gaps) one line, each front running a hair into the next
            const z = CASTLE_Z + dz, top = floor + height;
            poly( [ P( x0, floor, z ), P( x1 + 0.6, floor, z ), P( x1 + 0.6, top, z ), P( x0, top, z ) ], lit( floor, top, z, x0 ) );
            // (owner: a tick darker) a thin veil of the night over the lit front
            poly( [ P( x0, floor, z ), P( x1, floor, z ), P( x1, top, z ), P( x0, top, z ) ], this.rgba( inAir( [ 40, 26, 30 ], z, 0.3 ), 0.16 ) );
            // the roof: a long dark hip
            // the roof runs straight on into its neighbour's; hipped only at the row's two ends
            const hipL = x0 === CASTLE_BLOCKS[ 0 ][ 0 ] ? 5 : 0, hipR = x1 === CASTLE_BLOCKS[ CASTLE_BLOCKS.length - 1 ][ 1 ] ? 5 : -0.6;
            poly( [ P( x0 - 1, top, z ), P( x1 + 1, top, z ), P( x1 - hipR, top + 8, z + 9 ), P( x0 + hipL, top + 8, z + 9 ) ], this.rgba( inAir( [ 46, 40, 48 ], z, 0.45 ), 1 ) );
            // (kaizen) dormers along the roof, each a small dark gable with a lit window
            for ( let x = x0 + 8; x < x1 - 8; x += 12 ) {
              poly( [ P( x - 1.6, top + 1, z + 2 ), P( x + 1.6, top + 1, z + 2 ), P( x + 1.6, top + 3.4, z + 2 ), P( x, top + 4.8, z + 2 ), P( x - 1.6, top + 3.4, z + 2 ) ], this.rgba( inAir( [ 54, 46, 52 ], z, 0.45 ), 1 ) );
              const a = P( x - 0.6, top + 1.6, z + 1.9 ), b = P( x + 0.6, top + 3, z + 1.9 );
              const v = r();
              g.fillStyle = this.rgba( inAir( [ 255, 220, 150 ], z, 0.35 ), bright ? ( v < 0.5 ? 0.9 : 0.3 ) : ( v < 0.25 ? 0.5 : 0.18 ) );
              g.fillRect( a[ 0 ], b[ 1 ], Math.max( 0.4, b[ 0 ] - a[ 0 ] ), Math.max( 0.4, a[ 1 ] - b[ 1 ] ) );
            }
            // pilasters dividing the front into bays, a light cornice under the eaves, a darker plinth
            for ( let x = x0 + 18; x < x1 - 6; x += 18 ) {
              poly( [ P( x, floor, z - 0.2 ), P( x + 1.2, floor, z - 0.2 ), P( x + 1.2, top - 1, z - 0.2 ), P( x, top - 1, z - 0.2 ) ], this.rgba( inAir( this.mix( gold, [ 255, 236, 190 ], 0.4 ), z, 0.35 ), 0.35 ) );
            }
            poly( [ P( x0, top - 1.1, z - 0.3 ), P( x1, top - 1.1, z - 0.3 ), P( x1, top, z - 0.3 ), P( x0, top, z - 0.3 ) ], this.rgba( inAir( this.mix( pale, [ 255, 244, 214 ], 0.4 ), z, 0.35 ), 1 ) );
            poly( [ P( x0, floor, z - 0.2 ), P( x1, floor, z - 0.2 ), P( x1, floor + 2.2, z - 0.2 ), P( x0, floor + 2.2, z - 0.2 ) ], this.rgba( inAir( this.mix( warm, [ 120, 70, 40 ], 0.4 ), z, 0.35 ), 0.7 ) );
            // the long front's middle stands out a little, taller, under a pediment
            if ( x1 - x0 > 200 ) {
              const m = ( x0 + x1 ) / 2, zm = z - 2.5, tm = top + 3;
              poly( [ P( m - 26, floor, zm ), P( m + 26, floor, zm ), P( m + 26, tm, zm ), P( m - 26, tm, zm ) ], lit( floor, tm, zm, m, 0.9 ) );
              poly( [ P( m - 27, tm, zm ), P( m + 27, tm, zm ), P( m, tm + 6, zm ) ], this.rgba( inAir( this.mix( gold, warm, 0.25 ), zm, 0.35 ), 1 ) );
              poly( [ P( m - 27, tm - 0.6, zm - 0.1 ), P( m + 27, tm - 0.6, zm - 0.1 ), P( m + 27, tm, zm - 0.1 ), P( m - 27, tm, zm - 0.1 ) ], this.rgba( inAir( this.mix( pale, [ 255, 244, 214 ], 0.4 ), zm, 0.35 ), 1 ) );
            }
            const floors = height > 20 ? 5 : 4;
            for ( let fl = 0; fl < floors; fl++ ) {
              const y0 = floor + 2.6 + fl * ( height - 4 ) / floors;
              for ( let x = x0 + 3; x < x1 - 3; x += 4.2 ) {
                const zw = x1 - x0 > 200 && Math.abs( x + 0.9 - ( x0 + x1 ) / 2 ) < 25 ? z - 2.6 : z;
                const a = P( x, y0, zw - 0.1 ), b = P( x + 1.8, y0 + 2.4, zw - 0.1 );
                // (owner) few and dim at rest everywhere, most of them lit on the charge
                const on = r() < ( bright ? 0.7 : 0.3 );
                g.fillStyle = on ? this.rgba( inAir( this.mix( pale, [ 255, 240, 200 ], 0.5 ), z, 0.35 ), bright ? 0.95 : 0.55 ) : this.rgba( inAir( [ 140, 90, 50 ], z, 0.45 ), 0.6 );
                g.fillRect( a[ 0 ], Math.min( a[ 1 ], b[ 1 ] ), Math.max( 0.5, b[ 0 ] - a[ 0 ] ), Math.max( 0.5, Math.abs( a[ 1 ] - b[ 1 ] ) ) );
              }
            }
          }
          // the dark tower at the east end, its steep roof
          {
            const x0 = 600, x1 = 622, z = CASTLE_Z - 4, top = floor + 26;
            poly( [ P( x0, floor, z ), P( x1, floor, z ), P( x1, top, z ), P( x0, top, z ) ], lit( floor, top, z, x0, 0.8 ) );
            poly( [ P( x0 - 1, top, z ), P( x1 + 1, top, z ), P( ( x0 + x1 ) / 2, top + 16, z ) ], this.rgba( inAir( [ 34, 32, 42 ], z, 0.4 ), 1 ) );
          }
          // the gardens' walls and terraces under the fronts, lit too
          poly( [ P( -1040, floor - 12, CASTLE_Z - 24 ), P( 860, floor - 12, CASTLE_Z - 24 ), P( 860, floor, CASTLE_Z - 24 ), P( -1040, floor, CASTLE_Z - 24 ) ], lit( floor - 12, floor, CASTLE_Z - 24, 0, 1.4 ) );
        }
        // the gardens' trees down the slope: dark crowns, a little lighter where the lamps reach them
        const r = rr( 41 );
        for ( let row = 0; row < 7; row++ ) {
          const zz = z - row * 32;
          for ( let x = -half( zz ); x < half( zz ); x += 9 + r() * 10 ) {
            const top = ridge( x ) * ( 1 - row * 0.13 ) - 2;
            if ( top < 4 ) { continue; }
            const [ px, py ] = P( x, top, zz ), s = F / zz, rad = ( 4 + r() * 4 ) * s;
            const col = inAir( this.mix( [ 20, 40, 40 ], [ 36, 60, 50 ], r() ), zz, 0.5 );
            g.fillStyle = this.rgba( col, 1 );
            g.beginPath(); g.ellipse( px, py, rad, rad * 0.8, 0, 0, Math.PI * 2 ); g.fill();
          }
        }
        // (kaizen) Malá Strana climbing the hill: rows of houses stepping up the slope far to near, their
        // steep dark red roofs in tiers, chimneys, a few lit windows
        for ( let row = 0; row < 6; row++ ) {
          const zz = HILL_Z - 60 - row * 38;
          const lift = ( zz - BANK_Z ) / ( HILL_Z - BANK_Z );
          for ( let x = -half( zz ); x < half( zz ); ) {
            const w = 11 + r() * 13, h = 10 + r() * 6, y0 = Math.max( 0, ridge( x ) * lift - 6 - r() * 3 );
            const face = g.createLinearGradient( 0, P( x, y0, zz )[ 1 ], 0, P( x, y0 + h, zz )[ 1 ] );
            face.addColorStop( 0, this.rgba( inAir( this.mix( [ 210, 170, 120 ], gold, 0.3 ), zz, 0.45 ), 1 ) );
            face.addColorStop( 1, this.rgba( inAir( [ 96, 66, 56 ], zz, 0.45 ), 1 ) );
            poly( [ P( x, y0, zz ), P( x + w, y0, zz ), P( x + w, y0 + h, zz ), P( x, y0 + h, zz ) ], face );
            const rh = 5 + r() * 4, roof = this.rgba( inAir( this.mix( [ 72, 36, 32 ], [ 40, 30, 34 ], r() ), zz, 0.45 ), 1 );
            poly( [ P( x - 0.6, y0 + h, zz ), P( x + w + 0.6, y0 + h, zz ), P( x + w - 1.5, y0 + h + rh, zz + 6 ), P( x + 1.5, y0 + h + rh, zz + 6 ) ], roof );
            if ( r() < 0.5 ) { const cx = x + w * ( 0.2 + r() * 0.6 ); poly( [ P( cx, y0 + h + rh * 0.4, zz + 3 ), P( cx + 1.2, y0 + h + rh * 0.4, zz + 3 ), P( cx + 1.2, y0 + h + rh + 1.5, zz + 3 ), P( cx, y0 + h + rh + 1.5, zz + 3 ) ], roof ); }
            // (owner: not so many towers in the middle) no church towers among the roofs; the draw kept so
            // the houses stay as they were
            r();
            if ( r() < 0.6 ) { windows( x, x + w, y0, y0 + h, zz, r ); }
            x += w + ( r() < 0.15 ? 3 : 0 );
          }
        }
      }
      // the far bank: Malá Strana's embankment houses, their fronts lit warm, their roofs dark, the trees of
      // Kampa between them; on to the bridge and beyond it
      {
        const r = rr( 67 );
        const hues: RGB[] = [ [ 236, 204, 150 ], [ 224, 186, 140 ], [ 214, 196, 170 ], [ 230, 170, 120 ], [ 206, 180, 150 ] ];
        for ( let x = -half( BANK_Z ); x < half( BANK_Z ); ) {
          const w = 12 + r() * 14, h = 13 + r() * 10, z = BANK_Z + ( x > BRIDGE_B.x ? 30 : 0 ) + r() * 6;
          if ( x > -330 && x < -190 && r() < 0.8 ) {
            // Kampa's trees: round dark crowns over the embankment
            const [ px, py ] = P( x + w / 2, 10 + r() * 6, z - 10 ), s = F / z, rad = ( 7 + r() * 5 ) * s;
            g.fillStyle = this.rgba( inAir( this.mix( [ 22, 44, 40 ], [ 40, 66, 52 ], r() ), z, 0.4 ), 1 );
            g.beginPath(); g.ellipse( px, py, rad, rad * 0.85, 0, 0, Math.PI * 2 ); g.fill();
            x += w * 0.7;
            continue;
          }
          const hue = hues[ Math.floor( r() * hues.length ) ];
          const face = g.createLinearGradient( 0, P( x, 0, z )[ 1 ], 0, P( x, h, z )[ 1 ] );
          face.addColorStop( 0, this.rgba( inAir( this.mix( hue, gold, 0.35 ), z, 0.35 ), 1 ) );
          face.addColorStop( 1, this.rgba( inAir( this.mix( hue, [ 90, 60, 50 ], 0.45 ), z, 0.35 ), 1 ) );
          poly( [ P( x, 0, z ), P( x + w, 0, z ), P( x + w, h, z ), P( x, h, z ) ], face );
          // the roof: a steep dark pitch, some with a dormer
          const rh = 6 + r() * 4;
          poly( [ P( x - 0.4, h, z ), P( x + w + 0.4, h, z ), P( x + w - 2, h + rh, z + 6 ), P( x + 2, h + rh, z + 6 ) ], this.rgba( inAir( [ 58, 34, 32 ], z, 0.4 ), 1 ) );
          windows( x, x + w, 2, h - 1, z, r );
          x += w + ( r() < 0.2 ? 2 : 0 );
        }
        // the embankment wall at the water, lit along its top
        poly( [ P( -half( BANK_Z ), 0, BANK_Z - 8 ), P( half( BANK_Z ), 0, BANK_Z - 8 ), P( half( BANK_Z ), 2.4, BANK_Z - 8 ), P( -half( BANK_Z ), 2.4, BANK_Z - 8 ) ], this.rgba( inAir( this.mix( warm, [ 70, 50, 46 ], 0.5 ), BANK_Z, 0.4 ), 1 ) );
        // (owner's photograph) tall dark lindens along the bank before the houses (behind the bridge), warm where the lamps
        // under them reach their crowns; the lamps' small glows along the water
        const r2 = rr( 71 ), tz = BANK_Z - 14;
        // (owner) all along the bank behind the bridge, edge to edge
        for ( let x = -half( tz ); x < half( tz ); x += 8 + r2() * 9 ) {
          const hgt = 18 + r2() * 10, wide = 8 + r2() * 4, s2 = F / tz;
          const [ bx, by ] = P( x, 0, tz ), [ , ty ] = P( x, hgt, tz );
          const crownH = by - ty;
          g.fillStyle = this.rgba( inAir( [ 30, 28, 26 ], tz, 0.3 ), 1 );
          // (owner: the bridge's order) a tall bare trunk under a crown held high, so through the arches one sees
          // the trunks and the lit bank, not leaves
          g.fillRect( bx - s2 * 0.45, by - crownH * 0.5, s2 * 0.9, crownH * 0.5 );
          // (kaizen: no lollipops) the crown's dark mass first, an upright ovoid down to a short trunk
          g.fillStyle = this.rgba( inAir( [ 16, 32, 28 ], tz, 0.25 ), 1 );
          g.beginPath(); g.ellipse( bx, by - crownH * 0.72, wide * s2, Math.abs( crownH ) * 0.3, 0, 0, Math.PI * 2 ); g.fill();
          // then its clumps: dark above, lit warm beneath where the lamps reach, a few catching the sky's blue
          for ( let q = 0; q < 46; q++ ) {
            const a = r2() * Math.PI * 2, d = Math.sqrt( r2() );
            const cx = bx + Math.cos( a ) * d * wide * s2 * 0.92, cy = by - crownH * 0.72 + Math.sin( a ) * d * crownH * 0.27;
            const rad = s2 * ( 1.4 + r2() * 1.6 ), low = Math.max( 0, ( cy - ( by - crownH ) ) / crownH );
            const tone = this.mix( this.mix( [ 30, 58, 44 ], [ 44, 70, 72 ], r2() * 0.3 ), [ 160, 132, 60 ], Math.pow( low, 2.2 ) * 0.75 );
            const cg = g.createRadialGradient( cx, cy - rad * 0.3, 0, cx, cy, rad );
            cg.addColorStop( 0, this.rgba( inAir( tone, tz, 0.25 ), 1 ) );
            cg.addColorStop( 1, this.rgba( inAir( this.mix( tone, [ 12, 26, 24 ], 0.6 ), tz, 0.25 ), 1 ) );
            g.fillStyle = cg;
            g.beginPath(); g.arc( cx, cy, rad, 0, Math.PI * 2 ); g.fill();
          }
        }
        for ( let x = -half( BANK_Z ); x < half( BANK_Z ); x += 34 ) {
          const [ lx, ly ] = P( x, 4.2, BANK_Z - 10 ), s2 = F / BANK_Z;
          const halo = g.createRadialGradient( lx, ly, 0, lx, ly, s2 * 2.4 );
          halo.addColorStop( 0, 'rgba(255,232,170,0.9)' );
          halo.addColorStop( 0.3, this.rgba( gold, 0.4 ) );
          halo.addColorStop( 1, this.rgba( gold, 0 ) );
          g.fillStyle = halo;
          g.fillRect( lx - s2 * 2.4, ly - s2 * 2.4, s2 * 4.8, s2 * 4.8 );
        }
      }
    };
    // rows of windows on a lit facade: most lit warm, some dark
    const windows = ( x0: number, x1: number, y0: number, y1: number, z: number, r: () => number ) => {
      const s = F / z;
      if ( s < 0.35 ) { return; }
      for ( let y = y0 + 1.5; y < y1 - 2.5; y += 3.4 ) {
        for ( let x = x0 + 1.5; x < x1 - 1.6; x += 3 ) {
          const a = P( x, y, z - 0.1 ), b = P( x + 1.2, y + 1.9, z - 0.1 );
          const on = r() < ( bright ? 0.62 : 0.28 );
          g.fillStyle = on ? this.rgba( inAir( [ 255, 224, 150 ], z, 0.3 ), bright ? 0.9 : 0.5 ) : this.rgba( inAir( [ 70, 50, 46 ], z, 0.4 ), 0.55 );
          g.fillRect( a[ 0 ], Math.min( a[ 1 ], b[ 1 ] ), Math.max( 0.4, b[ 0 ] - a[ 0 ] ), Math.max( 0.4, Math.abs( a[ 1 ] - b[ 1 ] ) ) );
        }
      }
    };

    // the gas lamps found while baking the bridge: world x, z; screen x, y of the flame; pixels to the metre
    const lamps: [ number, number, number, number, number ][] = [];
    // ---- Charles Bridge ----
    // (owner: drawn again from the start, after the photograph) square to the view along the far bank: one
    // stone face with the arches cut out of it, each a true circular arc springing low over the water; a ring
    // of voussoirs round every arch; the pointed cutwaters before the piers with their sloping caps; the
    // dark wet band at the water; the string course at the deck and the parapet's coping; the lamps' warm
    // light pooled on the stone under them; through every opening the barrel's dark underside and the
    // piers' side walls running back under the bridge
    const bridge = () => {
      const zf = BRIDGE_A.z - BRIDGE_W / 2, zb = BRIDGE_A.z + BRIDGE_W / 2;
      const Pf = ( x: number, y: number ) => P( x, y, zf ), Pb = ( x: number, y: number ) => P( x, y, zb );
      const k = F / zf;
      const SPAN = 22, PIER = 9, SPRING = 2.4, RISE = 8.6, step = SPAN + PIER;
      const R = ( SPAN * SPAN / 4 + RISE * RISE ) / ( 2 * RISE ), cyArc = SPRING + RISE - R;
      const reach = half( zb ) + 40;
      // the spans' left springings, laid from the axis out both ways so one arch sits under the words
      const spans: number[] = [];
      for ( let x = -SPAN / 2 - Math.ceil( reach / step ) * step; x < reach; x += step ) { spans.push( x ); }
      const arcPts = ( x0: number, rad: number, face: ( x: number, y: number ) => [ number, number ] ) => {
        const xc = x0 + SPAN / 2, a0 = Math.asin( Math.min( 1, ( SPAN / 2 ) / rad ) );
        const pts: [ number, number ][] = [];
        for ( let q = 0; q <= 24; q++ ) {
          const a = -a0 + 2 * a0 * q / 24;
          pts.push( face( xc + Math.sin( a ) * rad, cyArc + Math.cos( a ) * rad ) );
        }
        return pts;
      };
      const opening = ( x0: number ) => [ Pf( x0, 0 ), ...arcPts( x0, R, Pf ), Pf( x0 + SPAN, 0 ) ];
      const tone = ( c: RGB, a = 1 ) => this.rgba( inAir( c, zf, 0.3 ), a );
      // 1: inside every opening, clipped to it: the piers' side walls, then the barrel's underside
      for ( const x0 of spans ) {
        g.save();
        g.beginPath();
        opening( x0 ).forEach( ( [ x, y ], q ) => q ? g.lineTo( x, y ) : g.moveTo( x, y ) );
        g.closePath();
        g.clip();
        for ( const xs of [ x0, x0 + SPAN ] ) {
          poly( [ Pf( xs, 0 ), Pb( xs, 0 ), Pb( xs, SPRING ), Pf( xs, SPRING ) ], tone( this.mix( [ 84, 68, 60 ], warm, 0.15 ) ) );
        }
        const front = arcPts( x0, R, Pf ), back = arcPts( x0, R, Pb );
        const ug = g.createLinearGradient( 0, Pf( x0, SPRING )[ 1 ], 0, Pf( x0, SPRING + RISE )[ 1 ] );
        ug.addColorStop( 0, tone( this.mix( [ 92, 70, 58 ], warm, 0.25 ) ) );
        ug.addColorStop( 1, tone( [ 40, 30, 32 ] ) );
        g.fillStyle = ug;
        g.beginPath();
        front.forEach( ( [ x, y ], q ) => q ? g.lineTo( x, y ) : g.moveTo( x, y ) );
        back.slice().reverse().forEach( ( [ x, y ] ) => g.lineTo( x, y ) );
        g.closePath();
        g.fill();
        g.restore();
      }
      // 2: the face: one wall from end to end, the openings cut out of it
      const faceL = spans[ 0 ] - PIER, faceR = spans[ spans.length - 1 ] + SPAN + PIER;
      const facePath = () => {
        g.beginPath();
        [ Pf( faceL, 0 ), Pf( faceR, 0 ), Pf( faceR, PARAPET ), Pf( faceL, PARAPET ) ].forEach( ( [ x, y ], q ) => q ? g.lineTo( x, y ) : g.moveTo( x, y ) );
        g.closePath();
        for ( const x0 of spans ) {
          opening( x0 ).forEach( ( [ x, y ], q ) => q ? g.lineTo( x, y ) : g.moveTo( x, y ) );
          g.closePath();
        }
      };
      const fg = g.createLinearGradient( 0, Pf( 0, 0 )[ 1 ], 0, Pf( 0, PARAPET )[ 1 ] );
      fg.addColorStop( 0, tone( [ 52, 44, 44 ] ) );
      fg.addColorStop( 0.12, tone( [ 86, 70, 62 ] ) );
      fg.addColorStop( 0.6, tone( this.mix( [ 122, 98, 80 ], warm, 0.2 ) ) );
      fg.addColorStop( 1, tone( this.mix( [ 150, 120, 88 ], gold, 0.35 ) ) );
      facePath();
      g.fillStyle = fg;
      g.fill( 'evenodd' );
      g.save();
      facePath();
      g.clip( 'evenodd' );
      // the lamps' light pooled on the stone under each one, over the arch's crown
      for ( const x0 of spans ) {
        const [ lx, ly ] = Pf( x0 + SPAN / 2, PARAPET + 3 );
        const pool = g.createRadialGradient( lx, ly, 0, lx, ly, k * 9 );
        pool.addColorStop( 0, this.rgba( this.mix( gold, [ 255, 230, 170 ], 0.3 ), 0.5 ) );
        pool.addColorStop( 1, this.rgba( gold, 0 ) );
        g.fillStyle = pool;
        g.fillRect( lx - k * 9, ly - k * 9, k * 18, k * 18 );
      }
      // the stone's courses: faint joints above the crowns and down the piers
      g.strokeStyle = 'rgba(30,22,22,0.25)';
      g.lineWidth = Math.max( 0.4, k * 0.06 );
      for ( let y = 1.4; y < PARAPET - 0.8; y += 1.15 ) {
        const a = Pf( faceL, y ), b = Pf( faceR, y );
        g.beginPath(); g.moveTo( a[ 0 ], a[ 1 ] ); g.lineTo( b[ 0 ], b[ 1 ] ); g.stroke();
      }
      // the wet dark band at the water
      const wet = g.createLinearGradient( 0, Pf( 0, 0 )[ 1 ], 0, Pf( 0, 1.4 )[ 1 ] );
      wet.addColorStop( 0, tone( [ 26, 24, 30 ], 0.8 ) );
      wet.addColorStop( 1, tone( [ 26, 24, 30 ], 0 ) );
      g.fillStyle = wet;
      const w0 = Pf( faceL, 1.4 ), w1 = Pf( faceR, 0 );
      g.fillRect( w0[ 0 ], Math.min( w0[ 1 ], w1[ 1 ] ), w1[ 0 ] - w0[ 0 ], Math.abs( w1[ 1 ] - w0[ 1 ] ) );
      g.restore();
      // 3: the ring of voussoirs round each arch, a shade lighter, its stones' joints radiating
      for ( const x0 of spans ) {
        const inner = arcPts( x0, R, Pf ), outer = arcPts( x0, R + 1.1, Pf );
        poly( [ ...inner, ...outer.slice().reverse() ], tone( this.mix( [ 140, 112, 86 ], gold, 0.15 ) ) );
        g.strokeStyle = 'rgba(40,28,26,0.35)';
        g.lineWidth = Math.max( 0.4, k * 0.07 );
        for ( let q = 1; q < 24; q += 2 ) {
          g.beginPath(); g.moveTo( inner[ q ][ 0 ], inner[ q ][ 1 ] ); g.lineTo( outer[ q ][ 0 ], outer[ q ][ 1 ] ); g.stroke();
        }
      }
      // 4: the string course along the deck and the parapet's coping, catching the lamps' light
      poly( [ Pf( faceL, DECK - 0.7 ), Pf( faceR, DECK - 0.7 ), Pf( faceR, DECK ), Pf( faceL, DECK ) ], tone( this.mix( gold, [ 200, 170, 130 ], 0.5 ) ) );
      poly( [ Pf( faceL, DECK - 0.95 ), Pf( faceR, DECK - 0.95 ), Pf( faceR, DECK - 0.7 ), Pf( faceL, DECK - 0.7 ) ], tone( [ 40, 30, 30 ], 0.6 ) );
      poly( [ Pf( faceL, PARAPET - 0.45 ), Pf( faceR, PARAPET - 0.45 ), Pf( faceR, PARAPET ), Pf( faceL, PARAPET ) ], tone( this.mix( pale, [ 255, 240, 210 ], 0.35 ) ) );
      // 5: the cutwaters before the piers: a pointed nose of stone to the river, lit on its left face,
      // shaded on its right, under a sloping cap that rises back to the pier
      for ( const x0 of spans ) {
        const xl = x0 + SPAN, xr = xl + PIER, xm = ( xl + xr ) / 2, zt = zf - 5.5;
        const T = ( y: number ) => P( xm, y, zt );
        const lface = g.createLinearGradient( 0, Pf( xl, 0 )[ 1 ], 0, Pf( xl, 6 )[ 1 ] );
        lface.addColorStop( 0, tone( [ 50, 42, 42 ] ) );
        lface.addColorStop( 1, tone( this.mix( [ 128, 102, 82 ], warm, 0.2 ) ) );
        poly( [ Pf( xl, 0 ), T( 0 ), T( 5.6 ), Pf( xl, 5.6 ) ], lface );
        poly( [ T( 0 ), Pf( xr, 0 ), Pf( xr, 5.6 ), T( 5.6 ) ], tone( [ 66, 54, 52 ] ) );
        poly( [ Pf( xl, 5.6 ), T( 5.6 ), Pf( xm, 8.4 ) ], tone( this.mix( [ 150, 120, 90 ], gold, 0.25 ) ) );
        poly( [ T( 5.6 ), Pf( xr, 5.6 ), Pf( xm, 8.4 ) ], tone( [ 90, 74, 66 ] ) );
        // the river breaking at the nose
        const [ tx, ty ] = T( 0 );
        const foam = g.createRadialGradient( tx, ty, 0, tx, ty, k * 2.4 );
        foam.addColorStop( 0, 'rgba(210,226,250,0.35)' );
        foam.addColorStop( 1, 'rgba(210,226,250,0)' );
        g.fillStyle = foam;
        g.save(); g.translate( tx, ty ); g.scale( 1, 0.3 ); g.translate( -tx, -ty );
        g.fillRect( tx - k * 2.4, ty - k * 2.4, k * 4.8, k * 4.8 );
        g.restore();
      }
      // 6: the statues over the piers, lit gold by their own floodlights; the gas lamps over the crowns
      for ( const x0 of spans ) {
        const xs = x0 + SPAN + PIER / 2;
        const base = Pf( xs, PARAPET ), top = Pf( xs, PARAPET + 2.2 );
        g.fillStyle = tone( this.mix( gold, [ 120, 80, 40 ], 0.35 ) );
        g.fillRect( base[ 0 ] - k * 0.9, Math.min( base[ 1 ], top[ 1 ] ), k * 1.8, Math.abs( base[ 1 ] - top[ 1 ] ) );
        const fy = top[ 1 ], hgt = ( top[ 1 ] - base[ 1 ] ) / 2.2 * 4.2;
        g.beginPath();
        g.moveTo( base[ 0 ] - k * 0.75, fy );
        g.quadraticCurveTo( base[ 0 ] - k * 0.9, fy + hgt * 0.5, base[ 0 ] - k * 0.45, fy + hgt * 0.8 );
        g.lineTo( base[ 0 ] + k * 0.45, fy + hgt * 0.8 );
        g.quadraticCurveTo( base[ 0 ] + k * 0.9, fy + hgt * 0.5, base[ 0 ] + k * 0.75, fy );
        g.closePath();
        g.fill();
        g.beginPath(); g.arc( base[ 0 ], fy + hgt * 0.9, k * 0.35, 0, Math.PI * 2 ); g.fill();
        // the gas lamp over the next crown: a cast iron post, its collar, the six-sided lantern under a cap
        const lxw = x0 + SPAN / 2;
        const lk = k * 1.8, lb = Pf( lxw, PARAPET ), lt = Pf( lxw, PARAPET + 5.6 );
        const iron = tone( [ 28, 26, 30 ] );
        g.fillStyle = iron;
        g.fillRect( lb[ 0 ] - lk * 0.07, Math.min( lt[ 1 ], lb[ 1 ] ), Math.max( 0.5, lk * 0.14 ), Math.abs( lb[ 1 ] - lt[ 1 ] ) );
        if ( mirror ) { continue; }
        g.fillRect( lb[ 0 ] - lk * 0.16, lb[ 1 ] - lk * 0.5, Math.max( 0.6, lk * 0.32 ), lk * 0.5 );
        const gx = lt[ 0 ], gy = lt[ 1 ], gw0 = lk * 0.32, gw1 = lk * 0.46, gh = lk * 0.95;
        g.beginPath(); g.moveTo( gx - gw0 - lk * 0.06, gy ); g.lineTo( gx + gw0 + lk * 0.06, gy ); g.lineTo( gx + gw1 + lk * 0.06, gy - gh ); g.lineTo( gx - gw1 - lk * 0.06, gy - gh ); g.closePath(); g.fill();
        g.fillStyle = this.rgba( this.mix( gold, [ 255, 230, 170 ], 0.4 ), 0.85 );
        g.beginPath(); g.moveTo( gx - gw0, gy - lk * 0.05 ); g.lineTo( gx + gw0, gy - lk * 0.05 ); g.lineTo( gx + gw1, gy - gh + lk * 0.05 ); g.lineTo( gx - gw1, gy - gh + lk * 0.05 ); g.closePath(); g.fill();
        g.fillStyle = iron;
        g.fillRect( gx - lk * 0.03, gy - gh, Math.max( 0.4, lk * 0.06 ), gh );
        g.beginPath(); g.ellipse( gx, gy - gh, gw1 + lk * 0.12, lk * 0.2, 0, Math.PI, 0 ); g.fill();
        g.beginPath(); g.moveTo( gx - lk * 0.07, gy - gh - lk * 0.18 ); g.lineTo( gx + lk * 0.07, gy - gh - lk * 0.18 ); g.lineTo( gx, gy - gh - lk * 0.55 ); g.closePath(); g.fill();
        lamps.push( [ lxw, zf, gx, gy - gh * 0.5, lk ] );
      }
    };

    // lay a mirrored canvas on the water below the line `top`, in thin bands that tremble sideways
    const reflect = ( dst: CanvasRenderingContext2D, src: HTMLCanvasElement, top: number, alpha: number ) => {
      const rnd = this.seededLocal( 91 );
      dst.save();
      dst.setTransform( 1, 0, 0, 1, 0, 0 );
      const step = Math.max( 1, Math.round( 2 * scale ) );
      for ( let y = Math.floor( top * scale ); y < H * scale; y += step ) {
        const depth = ( y / scale - top ) / ( H - top );
        // (kaizen) a smooth sway with only a little grain, fading toward us
        const yy = y / scale;
        const off = ( Math.sin( yy * 0.23 ) * 1.1 + Math.sin( yy * 0.061 + 1.3 ) * 0.9 + ( rnd() - 0.5 ) * 0.7 ) * ( 0.4 + depth * 1.6 ) * scale;
        dst.globalAlpha = alpha * Math.max( 0, 1 - depth * 0.75 );
        dst.drawImage( src, 0, y, src.width, step, off, y, src.width, step );
      }
      dst.restore();
    };

    const buildBack = () => {
      // ---- the back layer ----
      const bk = canvas();
      g = bk.g;
      // the sky: deep blue at the top, lighter to the horizon, a few long dark clouds lying level
      const skyG = g.createLinearGradient( 0, 0, 0, hz );
      // (kaizen, owner's photograph) the blue hour's clear saturated blue, deepest overhead
      skyG.addColorStop( 0, this.rgba( this.mix( navy, [ 6, 16, 40 ], 0.4 ), 1 ) );
      skyG.addColorStop( 0.5, this.rgba( this.mix( navy, blue, 0.22 ), 1 ) );
      skyG.addColorStop( 1, this.rgba( this.mix( navy, blue, 0.55 ), 1 ) );
      g.fillStyle = skyG;
      g.fillRect( 0, 0, W, hz + 2 );
      {
        const r = rr( 13 );
        for ( let i = 0; i < 7; i++ ) {
          const cx = r() * W, cy = hz * ( 0.25 + r() * 0.5 ), cw = W * ( 0.12 + r() * 0.2 ), ch = H * ( 0.008 + r() * 0.012 );
          const cg = g.createRadialGradient( cx, cy, 0, cx, cy, cw );
          cg.addColorStop( 0, this.rgba( this.mix( navy, [ 10, 20, 40 ], 0.5 ), 0.55 ) );
          cg.addColorStop( 1, this.rgba( navy, 0 ) );
          g.save(); g.translate( cx, cy ); g.scale( 1, ch / cw ); g.translate( -cx, -cy );
          g.fillStyle = cg; g.fillRect( cx - cw, cy - cw, cw * 2, cw * 2 );
          g.restore();
        }
      }
      // the river: dark blue, a little lighter far off where it holds the sky
      const waterTop = this.P( 0, 0, BANK_Z - 8 )[ 1 ];
      const wg = g.createLinearGradient( 0, waterTop, 0, H );
      wg.addColorStop( 0, this.rgba( this.mix( navy, blue, 0.3 ), 1 ) );
      wg.addColorStop( 1, this.rgba( this.mix( navy, [ 2, 8, 20 ], 0.6 ), 1 ) );
      g.fillStyle = wg;
      g.fillRect( 0, waterTop, W, H - waterTop );
      // (owner's photograph) the weir across the river: the water beyond it a little paler, a soft pale line
      // where it spills over
      {
        const wz = 200, [ , wy ] = this.P( 0, 0, wz );
        const up = g.createLinearGradient( 0, waterTop, 0, wy );
        up.addColorStop( 0, this.rgba( this.mix( navy, blue, 0.3 ), 0 ) );
        up.addColorStop( 1, this.rgba( this.mix( navy, blue, 0.55 ), 0.55 ) );
        g.fillStyle = up;
        g.fillRect( 0, waterTop, W, wy - waterTop );
        const line = g.createLinearGradient( 0, wy - F / wz * 0.6, 0, wy + F / wz * 1.6 );
        line.addColorStop( 0, this.rgba( this.mix( blue, [ 255, 255, 255 ], 0.4 ), 0 ) );
        line.addColorStop( 0.35, this.rgba( this.mix( blue, [ 255, 255, 255 ], 0.4 ), 0.45 ) );
        line.addColorStop( 1, this.rgba( this.mix( blue, [ 255, 255, 255 ], 0.4 ), 0 ) );
        g.fillStyle = line;
        g.fillRect( 0, wy - F / wz * 0.6, W, F / wz * 2.2 );
      }
      // the far things' reflection, then the things themselves
      {
        const rf = canvas();
        const keep = g;
        g = rf.g;
        mirror = true;
        far();
        mirror = false;
        g = keep;
        reflect( g, rf.c, waterTop, 0.5 );
      }
      far();
      return bk.c;
    };
    this.back = buildBack();
    bright = true;
    this.backLit = buildBack();
    bright = false;

    // ---- the front layer: the bridge and its reflection ----
    const fr = canvas();
    {
      const rf = canvas();
      g = rf.g;
      mirror = true;
      bridge();
      mirror = false;
      g = fr.g;
      reflect( g, rf.c, this.P( 0, 0, BRIDGE_B.z )[ 1 ], 0.55 );
    }
    g = fr.g;
    bridge();
    this.front = fr.c;
    this.lamps = lamps.map( ( [ , , x, y, k ], i ) => [ x, y, k, i * 2.39 ] );
    // the light columns on the water fall exactly under the lamps
    this.columns = lamps.map( ( [ x, z ], i ) => [ x, z, i * 1.7 ] );
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The five colours of the theme's circle: Prague's navy, sky blue, warm orange, gold and pale gold. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 17, 62, 107 ], [ 116, 173, 248 ], [ 223, 134, 91 ], [ 239, 186, 90 ], [ 227, 202, 119 ] ];
  }
}
